import type {
  AddTaskInput,
  AddTaskResponse,
  AdStatusAction,
  AdStatusResponse,
  BoardResponse,
  CampaignAdsResponse,
  CampaignsResponse,
  AmazonCampaignsResponse,
  AmazonAdGroupsResponse,
  AmazonBidResponse,
  AmazonBudgetResponse,
  AmazonNegativeKeywordsResponse,
  AmazonNegativeMatchType,
  AmazonTargetsResponse,
  AmazonAdProduct,
  AmazonKwRankApplyInput,
  AmazonKwRankApplyResponse,
  AmazonKwRankResponse,
  AmazonKwRankRunResponse,
  GoogleBudgetResponse,
  GoogleCampaignsResponse,
  ChannelFilter,
  CreateInitiativeInput,
  CreateInitiativeResponse,
  DeferTaskResponse,
  InitiativeDetailResponse,
  InitiativesResponse,
  InitiativeStatus,
  KpisResponse,
  LineResponse,
  GcrResponse,
  MallAdsBidResponse,
  MallAdsResponse,
  RppBidConfig,
  RunMallAdsBidNowResponse,
  SalesResponse,
  SaveMallAdsBidConfigResponse,
  SaveMallAdsBidManualInput,
  SaveMallAdsBidManualResponse,
  SaveMallAdsBidProbeInput,
  SaveMallAdsBidProbeResponse,
  SaveMallAdsBidRuleInput,
  SaveMallAdsBidRuleResponse,
  SetInitiativeStatusResponse,
  SetTaskDoneResponse,
  SummaryResponse,
  SyncResponse,
  ToggleChecklistResponse,
  UpdateInitiativeInput,
  UpdateInitiativeResponse,
} from "../types/contract";
import { notifyUnauthenticated } from "../lib/authState";
import { isRole, type SessionUser } from "../lib/roles";
import type {
  AmzAiInput,
  AmzAiResponse,
  AmzHistoryResponse,
  AmzImportInput,
  AmzOverviewResponse,
  AmzPreviewResponse,
  AmzProductDetailResponse,
  AmzProductSyncResponse,
  AmzPublishInput,
  AmzPublishResponse,
  AmzSaveDraftInput,
  AmzSaveDraftResponse,
} from "../types/amazon";
import type {
  FinanceResponse,
  FreeeStatusResponse,
  InventoryAutoResponse,
  InventoryMonthEndRow,
  Mall,
  PlAdsResponse,
  PlImportsResponse,
  PlProductsResponse,
  PlResponse,
  PlSettingsResponse,
  PrepaymentsResponse,
} from "../types/pl";
import type { AnalyticsResponse } from "../types/analytics";
import type {
  ReviewCampaignsResponse,
  ReviewDraftResponse,
  ReviewFlowInput,
  ReviewFlowResponse,
  ReviewItemDetailResponse,
  ReviewListFilter,
  ReviewListResponse,
  ReviewOverviewResponse,
  ReviewPeriodMode,
  ReviewReplyResponse,
  ReviewReportGenerateResponse,
  ReviewReportResponse,
  ReviewRuleUpdateInput,
  ReviewRulesResponse,
  ReviewSlaHours,
  ReviewStoreFilter,
  ReviewUnqueueResponse,
} from "../types/reviews";

export class ApiClientError extends Error {
  status: number;
  code: string;
  /** 422等でサーバーが `error` オブジェクトに添える追加フィールド（例: open_checks/open_tasks）。 */
  details?: Record<string, unknown>;
  constructor(status: number, code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

function currentSearch(): URLSearchParams {
  return new URLSearchParams(window.location.search);
}

/** `?mock=1` が付いている間はfixtureのみで動作し、実APIには一切通信しない（契約AC-要件）。 */
export function isMockMode(): boolean {
  return currentSearch().get("mock") === "1";
}

const BFF_BASE = "/api/ad-console";

async function realFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BFF_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.ok === false) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? `リクエストに失敗しました（HTTP ${res.status}）`;
    if (res.status === 401) {
      // 実運用（非mock）でのみ発生しうる。App側にログイン画面への切替を通知する。
      notifyUnauthenticated();
    }
    // worker実装の実形は `{ ok:false, error:{code,message}, open_checks, open_tasks }` のように
    // 追加フィールドがトップレベルに乗る（error オブジェクトの中ではない）。details は
    // body 側の残余（ok/error以外）から取る。
    const { ok: _ok, error: _error, ...details } = (body ?? {}) as Record<string, unknown>;
    throw new ApiClientError(res.status, code, message, Object.keys(details).length ? details : undefined);
  }
  return body as T;
}

/**
 * mock/index.ts（と、そこが静的importするfixture JSON群）は、`?mock=1` の時だけ
 * 動的importで読み込む。これにより実運用ビルド（mock無し）のバンドルにfixtureのダミーデータを
 * 含めない（B5対応）。
 */
async function loadMock() {
  return import("./mock");
}

function wrapMock<T>(fn: (mock: Awaited<ReturnType<typeof loadMock>>) => Promise<T>): Promise<T> {
  return loadMock().then((mock) =>
    fn(mock).catch((e) => {
      if (e instanceof mock.MockHttpError) {
        // realFetchと同じ規約: 追加フィールドはbodyのトップレベル（ok/error以外）から取る。
        const { ok: _ok, error: _error, ...details } = e.body as unknown as Record<string, unknown>;
        throw new ApiClientError(
          e.status,
          e.body.error.code,
          e.body.error.message,
          Object.keys(details).length ? details : undefined
        );
      }
      throw e;
    })
  );
}

export async function fetchSummary(from: string, to: string): Promise<SummaryResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetSummary(currentSearch()));
  return realFetch<SummaryResponse>(`/summary?from=${from}&to=${to}`);
}

export async function fetchCampaigns(
  channel: ChannelFilter,
  q: string,
  range?: { from: string; to: string }
): Promise<CampaignsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetCampaigns(currentSearch(), channel, q));
  const params = new URLSearchParams({ channel, ...(q ? { q } : {}), ...(range ? { from: range.from, to: range.to } : {}) });
  return realFetch<CampaignsResponse>(`/campaigns?${params.toString()}`);
}

export async function fetchCampaignAds(
  campaignId: string,
  opts?: { range?: { from: string; to: string }; channel?: ChannelFilter }
): Promise<CampaignAdsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetCampaignAds(currentSearch(), campaignId));
  const params = new URLSearchParams();
  if (opts?.range) {
    params.set("from", opts.range.from);
    params.set("to", opts.range.to);
  }
  if (opts?.channel && opts.channel !== "all") params.set("channel", opts.channel);
  const qs = params.toString();
  return realFetch<CampaignAdsResponse>(`/campaigns/${encodeURIComponent(campaignId)}/ads${qs ? `?${qs}` : ""}`);
}

// ---- Amazon 広告（2026-10-23・P0 読み取り＋P1 操作系・worker /api/ad-console/amazon/*・BFF ALLOWLIST 同名） ----

/** Amazon 広告キャンペーン一覧（期間集計＋当日消化率）。チャネルタブ・検索語は呼び出し側で絞る。 */
export async function fetchAmazonCampaigns(from: string, to: string): Promise<AmazonCampaignsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonCampaigns(currentSearch()));
  const params = new URLSearchParams({ from, to });
  return realFetch<AmazonCampaignsResponse>(`/amazon/campaigns?${params.toString()}`);
}

function amazonQuery(opts?: { range?: { from: string; to: string }; product?: AmazonAdProduct | null }): string {
  const params = new URLSearchParams();
  if (opts?.range) {
    params.set("from", opts.range.from);
    params.set("to", opts.range.to);
  }
  if (opts?.product) params.set("product", opts.product);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/** キャンペーン配下の広告グループ（構造＋期間実績）。product を渡すと v1 query が1回で済む。 */
export async function fetchAmazonAdGroups(
  campaignId: string,
  opts?: { range?: { from: string; to: string }; product?: AmazonAdProduct | null }
): Promise<AmazonAdGroupsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonAdGroups(currentSearch(), campaignId));
  return realFetch<AmazonAdGroupsResponse>(`/amazon/campaigns/${encodeURIComponent(campaignId)}/adgroups${amazonQuery(opts)}`);
}

/** 広告グループ配下のターゲット（キーワード/商品/自動・除外含む）＋期間実績。 */
export async function fetchAmazonAdGroupTargets(
  adGroupId: string,
  opts?: { range?: { from: string; to: string }; product?: AmazonAdProduct | null }
): Promise<AmazonTargetsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonAdGroupTargets(currentSearch(), adGroupId));
  return realFetch<AmazonTargetsResponse>(`/amazon/adgroups/${encodeURIComponent(adGroupId)}/targets${amazonQuery(opts)}`);
}

// ---- Amazon 検索順位トラッカー（2026-11-05・worker /api/ad-console/amazon-kw-rank*・BFF ALLOWLIST 同名） ----

/** 監視 KW ごとの自然/広告順位・TOS IS・入札・提案・日別推移。 */
export async function fetchAmazonKwRank(from: string, to: string): Promise<AmazonKwRankResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonKwRank(currentSearch()));
  const params = new URLSearchParams({ from, to });
  return realFetch<AmazonKwRankResponse>(`/amazon-kw-rank?${params.toString()}`);
}

/** 提案の承認→反映（dry_run=true は Amazon に書かず現在値を読むだけ）。worker 側で許可リスト fail-closed・上限・読み戻し・台帳。 */
export async function applyAmazonKwRank(input: AmazonKwRankApplyInput): Promise<AmazonKwRankApplyResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockApplyAmazonKwRank(currentSearch(), input));
  return realFetch<AmazonKwRankApplyResponse>(`/amazon-kw-rank/apply`, { method: "POST", body: JSON.stringify(input) });
}

/** 手動で Apify run の開始 / TOS レポートの作成（取込は worker の 5 分 tick）。 */
export async function runAmazonKwRank(what: "apify" | "tos" | "both" = "both"): Promise<AmazonKwRankRunResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockRunAmazonKwRank(what));
  return realFetch<AmazonKwRankRunResponse>(`/amazon-kw-rank/run`, { method: "POST", body: JSON.stringify({ what }) });
}

/** Amazon キャンペーン本体の配信ON/OFF（Google と同型・worker 側で許可リスト fail-closed＋監査）。 */
export async function setAmazonCampaignStatus(
  campaignId: string,
  action: AdStatusAction,
  adProduct?: AmazonAdProduct | null
): Promise<AdStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetAmazonCampaignStatus(currentSearch(), campaignId, action));
  return realFetch<AdStatusResponse>(`/amazon/campaigns/${encodeURIComponent(campaignId)}/status`, {
    method: "POST",
    body: JSON.stringify({ action, ...(adProduct ? { ad_product: adProduct } : {}) }),
  });
}

/** Amazon キャンペーンの日予算（円・整数）。worker 側で上限 AMAZON_ADS_MAX_DAILY_BUDGET と許可キャンペーンを検証し、書込後に読み戻す。 */
export async function setAmazonCampaignBudget(
  campaignId: string,
  dailyBudget: number,
  adProduct?: AmazonAdProduct | null
): Promise<AmazonBudgetResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetAmazonCampaignBudget(currentSearch(), campaignId, dailyBudget));
  return realFetch<AmazonBudgetResponse>(`/amazon/campaigns/${encodeURIComponent(campaignId)}/budget`, {
    method: "POST",
    body: JSON.stringify({ daily_budget: dailyBudget, ...(adProduct ? { ad_product: adProduct } : {}) }),
  });
}

/** ターゲット（キーワード/商品/自動）の入札（円・整数）。worker 側で上限 AMAZON_ADS_MAX_BID と親キャンペーンの許可を検証。 */
export async function setAmazonTargetBid(targetId: string, bid: number, adProduct?: AmazonAdProduct | null): Promise<AmazonBidResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetAmazonTargetBid(currentSearch(), targetId, bid));
  return realFetch<AmazonBidResponse>(`/amazon/targets/${encodeURIComponent(targetId)}/bid`, {
    method: "POST",
    body: JSON.stringify({ bid, ...(adProduct ? { ad_product: adProduct } : {}) }),
  });
}

/** 広告グループに除外キーワードを追加（SP のみ・最大50件/回）。 */
export async function addAmazonNegativeKeywords(
  adGroupId: string,
  keywords: string[],
  matchType: AmazonNegativeMatchType,
  adProduct?: AmazonAdProduct | null
): Promise<AmazonNegativeKeywordsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockAddAmazonNegativeKeywords(currentSearch(), adGroupId, keywords, matchType));
  return realFetch<AmazonNegativeKeywordsResponse>(`/amazon/adgroups/${encodeURIComponent(adGroupId)}/negative-keywords`, {
    method: "POST",
    body: JSON.stringify({ keywords, match_type: matchType, ...(adProduct ? { ad_product: adProduct } : {}) }),
  });
}

// ---- Google 広告（2026-10-09・worker /api/ad-console/google/*・BFF ALLOWLIST 同名） ----

export async function fetchGoogleCampaigns(
  channel: ChannelFilter,
  q: string,
  range?: { from: string; to: string }
): Promise<GoogleCampaignsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetGoogleCampaigns(currentSearch(), channel, q));
  const params = new URLSearchParams({ channel, ...(q ? { q } : {}), ...(range ? { from: range.from, to: range.to } : {}) });
  return realFetch<GoogleCampaignsResponse>(`/google/campaigns?${params.toString()}`);
}

export async function fetchGoogleCampaignAds(
  campaignId: string,
  opts?: { range?: { from: string; to: string }; channel?: ChannelFilter }
): Promise<CampaignAdsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetGoogleCampaignAds(currentSearch(), campaignId));
  const params = new URLSearchParams();
  if (opts?.range) {
    params.set("from", opts.range.from);
    params.set("to", opts.range.to);
  }
  if (opts?.channel && opts.channel !== "all") params.set("channel", opts.channel);
  const qs = params.toString();
  return realFetch<CampaignAdsResponse>(`/google/campaigns/${encodeURIComponent(campaignId)}/ads${qs ? `?${qs}` : ""}`);
}

/** Google キャンペーン本体の配信ON/OFF（Meta の ad 単位と違いキャンペーン単位）。 */
export async function setGoogleCampaignStatus(campaignId: string, action: AdStatusAction): Promise<AdStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetGoogleCampaignStatus(currentSearch(), campaignId, action));
  return realFetch<AdStatusResponse>(`/google/campaigns/${encodeURIComponent(campaignId)}/status`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

/** Google キャンペーンの日予算（円・整数）。worker 側で上限 GOOGLE_ADS_MAX_DAILY_BUDGET と許可キャンペーンを検証。 */
export async function setGoogleCampaignBudget(campaignId: string, dailyBudget: number): Promise<GoogleBudgetResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetGoogleCampaignBudget(currentSearch(), campaignId, dailyBudget));
  return realFetch<GoogleBudgetResponse>(`/google/campaigns/${encodeURIComponent(campaignId)}/budget`, {
    method: "POST",
    body: JSON.stringify({ daily_budget: dailyBudget }),
  });
}

/** Google 広告（"<adGroupId>~<adId>"）／P-MAX アセットグループ（"ag_<id>"）の配信ON/OFF。 */
export async function setGoogleAdStatus(adId: string, action: AdStatusAction): Promise<AdStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetGoogleAdStatus(currentSearch(), adId, action));
  return realFetch<AdStatusResponse>(`/google/ads/${encodeURIComponent(adId)}/status`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export async function fetchSales(from: string, to: string): Promise<SalesResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetSales(currentSearch(), from, to));
  return realFetch<SalesResponse>(`/sales?from=${from}&to=${to}`);
}

export async function setAdStatus(
  adId: string,
  action: AdStatusAction
): Promise<AdStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetAdStatus(currentSearch(), adId, action));
  return realFetch<AdStatusResponse>(`/ads/${encodeURIComponent(adId)}/status`, {
    method: "POST",
    body: JSON.stringify({ action }),
  });
}

export async function fetchKpis(
  from: string,
  to: string,
  channel: ChannelFilter
): Promise<KpisResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetKpis(currentSearch(), channel));
  const params = new URLSearchParams({ from, to, channel });
  return realFetch<KpisResponse>(`/kpis?${params.toString()}`);
}

export async function fetchMallAds(): Promise<MallAdsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetMallAds(currentSearch()));
  return realFetch<MallAdsResponse>(`/mall-ads`);
}

// ---- 楽天RPP 順位自動入札（tmp/fable/2026-09-25-rpp-bid/plan.md §4・§6） ----

export async function fetchMallAdsBid(): Promise<MallAdsBidResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetMallAdsBid());
  return realFetch<MallAdsBidResponse>(`/mall-ads/bid`);
}

export async function saveMallAdsBidRule(input: SaveMallAdsBidRuleInput): Promise<SaveMallAdsBidRuleResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostMallAdsBidRule(input));
  return realFetch<SaveMallAdsBidRuleResponse>(`/mall-ads/bid/rules`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** value は文字列で渡す（enabledは"0"/"1"）。BFF/worker側の POST /mall-ads/bid/config 契約に合わせる。 */
export async function saveMallAdsBidConfig(
  key: keyof RppBidConfig,
  value: string
): Promise<SaveMallAdsBidConfigResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostMallAdsBidConfig(key, value));
  return realFetch<SaveMallAdsBidConfigResponse>(`/mall-ads/bid/config`, {
    method: "POST",
    body: JSON.stringify({ key, value }),
  });
}

/** v2: 手動CPC（反映は次回run）。cancel:true で反映待ち・ロックを解除。plan(v2) §3-4。 */
export async function saveMallAdsBidManual(input: SaveMallAdsBidManualInput): Promise<SaveMallAdsBidManualResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostMallAdsBidManual(input));
  return realFetch<SaveMallAdsBidManualResponse>(`/mall-ads/bid/manual`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** v2: 探索（意図的にCPCを段階的に下げて順位ラダーを調べる）の開始／中止。plan(v2) §3-4。 */
export async function saveMallAdsBidProbe(input: SaveMallAdsBidProbeInput): Promise<SaveMallAdsBidProbeResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostMallAdsBidProbe(input));
  return realFetch<SaveMallAdsBidProbeResponse>(`/mall-ads/bid/probe`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** 「今すぐ実行」: RPP入札の Cloud Run Job を今すぐ起動する（反映まで約3分）。失敗は 422 invalid_input / 502 upstream_error。 */
export async function runMallAdsBidNow(): Promise<RunMallAdsBidNowResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostMallAdsBidRunNow());
  return realFetch<RunMallAdsBidNowResponse>(`/mall-ads/bid/run-now`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function fetchLine(from: string, to: string): Promise<LineResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetLine(currentSearch()));
  return realFetch<LineResponse>(`/line?from=${from}&to=${to}`);
}

// ---- Google カスタマーレビュー仕込み（2026-10-19・購入完了ブロック） ----

export async function fetchGcr(from: string, to: string): Promise<GcrResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetGcr(from, to));
  return realFetch<GcrResponse>(`/gcr?from=${from}&to=${to}`);
}

/** MC「同意数」の週次手入力（week は週の月曜日 YYYY-MM-DD）。 */
export async function putGcrMcConsents(week: string, consents: number): Promise<{ ok: true; week: string; consents: number }> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPutGcrMc(week, consents));
  return realFetch(`/gcr/mc`, { method: "POST", body: JSON.stringify({ week, consents }) });
}

// ---- マーケ施策管理（T-MK-B・plan-mk.md §1.3） ----

export async function fetchInitiatives(from: string, to: string): Promise<InitiativesResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetInitiatives(currentSearch(), from, to));
  return realFetch<InitiativesResponse>(`/initiatives?from=${from}&to=${to}`);
}

export async function fetchInitiative(id: string): Promise<InitiativeDetailResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetInitiative(id));
  return realFetch<InitiativeDetailResponse>(`/initiatives/${encodeURIComponent(id)}`);
}

export async function createInitiative(input: CreateInitiativeInput): Promise<CreateInitiativeResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockCreateInitiative(input));
  return realFetch<CreateInitiativeResponse>(`/initiatives`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateInitiative(
  id: string,
  input: UpdateInitiativeInput
): Promise<UpdateInitiativeResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockUpdateInitiative(id, input));
  return realFetch<UpdateInitiativeResponse>(`/initiatives/${encodeURIComponent(id)}/update`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function setInitiativeStatus(
  id: string,
  status: InitiativeStatus,
  force?: boolean
): Promise<SetInitiativeStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetInitiativeStatus(id, status, force));
  return realFetch<SetInitiativeStatusResponse>(`/initiatives/${encodeURIComponent(id)}/status`, {
    method: "POST",
    body: JSON.stringify({ status, force: !!force, actor: "村瀬" }),
  });
}

export async function toggleChecklist(
  id: string,
  group: "before" | "during" | "after",
  index: number,
  done: boolean
): Promise<ToggleChecklistResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockToggleChecklist(id, group, index, done));
  return realFetch<ToggleChecklistResponse>(`/initiatives/${encodeURIComponent(id)}/checklist`, {
    method: "POST",
    body: JSON.stringify({ group, index, done }),
  });
}

export async function addTask(id: string, input: AddTaskInput): Promise<AddTaskResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockAddTask(id, input));
  return realFetch<AddTaskResponse>(`/initiatives/${encodeURIComponent(id)}/tasks`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function setTaskDone(id: string, done: boolean): Promise<SetTaskDoneResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSetTaskDone(id, done));
  return realFetch<SetTaskDoneResponse>(`/tasks/${encodeURIComponent(id)}/done`, {
    method: "POST",
    body: JSON.stringify({ done, actor: "村瀬" }),
  });
}

export async function deferTask(id: string, days = 1): Promise<DeferTaskResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockDeferTask(id, days));
  return realFetch<DeferTaskResponse>(`/tasks/${encodeURIComponent(id)}/defer`, {
    method: "POST",
    body: JSON.stringify({ days }),
  });
}

/**
 * 外部連携（Meta/LINE/楽天）の即時同期（T-MK2-B・plan-mk2.md §1.2）。「今すぐ更新」ボタンから呼ぶ。
 * INTERNAL_KEY 専用エンドポイント（READ_KEYは403）。呼び出し後は fetchInitiatives の再取得で
 * 反映（sources.last_synced_at・新規/更新された施策）を確認する。
 */
export async function syncInitiatives(): Promise<SyncResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSyncInitiatives(currentSearch()));
  return realFetch<SyncResponse>(`/initiatives/sync`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

// ---- 施策画面 v2（plan.md §3.6）: board / AIブリーフ / 判断カードの「済みにする」 ----

export async function fetchInitiativeBoard(): Promise<BoardResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetInitiativeBoard(currentSearch()));
  return realFetch<BoardResponse>(`/initiatives/board`);
}

/** autopilot を即時実行して AIブリーフを強制再生成する（INTERNAL_KEY 専用）。 */
export async function refreshInitiativeBrief(): Promise<BoardResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockRefreshInitiativeBrief());
  return realFetch<BoardResponse>(`/initiatives/brief/refresh`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** 判断カードを「済みにする」（INTERNAL_KEY 専用。条件が消えれば押さなくても自動で消える）。 */
export async function dismissAttention(id: string): Promise<{ ok: true }> {
  if (isMockMode()) return wrapMock((mock) => mock.mockDismissAttention(id));
  return realFetch<{ ok: true }>(`/initiatives/attention/dismiss`, {
    method: "POST",
    body: JSON.stringify({ id }),
  });
}

// ---- P/L（管理会計）ダッシュボード（stoq-worker /api/ad-console/pl/*） ----

export async function fetchPl(
  from: string,
  to: string,
  compare?: { from: string; to: string }
): Promise<PlResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPl(currentSearch(), from, to, compare));
  const params = new URLSearchParams({ from, to });
  if (compare) {
    params.set("compare_from", compare.from);
    params.set("compare_to", compare.to);
  }
  return realFetch<PlResponse>(`/pl?${params.toString()}`);
}

export async function fetchPlProducts(from: string, to: string, mall: Mall | "all"): Promise<PlProductsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlProducts(currentSearch(), from, to, mall));
  const params = new URLSearchParams({ from, to });
  if (mall !== "all") params.set("mall", mall);
  return realFetch<PlProductsResponse>(`/pl/products?${params.toString()}`);
}

export async function fetchPlAds(from: string, to: string, mall: Mall | "all"): Promise<PlAdsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlAds(currentSearch(), from, to, mall));
  const params = new URLSearchParams({ from, to });
  if (mall !== "all") params.set("mall", mall);
  return realFetch<PlAdsResponse>(`/pl/ads?${params.toString()}`);
}

export async function fetchPlImports(): Promise<PlImportsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlImports(currentSearch()));
  return realFetch<PlImportsResponse>(`/pl/imports`);
}

export async function fetchPlSettings(): Promise<PlSettingsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlSettings(currentSearch()));
  return realFetch<PlSettingsResponse>(`/pl/settings`);
}

export async function postPlSetting(key: string, value: string, confirmed: boolean): Promise<PlSettingsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlSetting(key, value, confirmed));
  return realFetch<PlSettingsResponse>(`/pl/settings`, {
    method: "POST",
    body: JSON.stringify({ key, value, confirmed }),
  });
}

// ---- P/L 取込（plan.md §4-2 POST /pl/import/*, /pl/logi-monthly） ----
// レスポンス型は worker 側 pl-types.ts に未定義のため、ここでローカルに定義する
// （types/pl.ts は固定済みファイルのため編集しない・workers-common.md 参照）。

export interface PostPlImportRakutenExpenseInput {
  month: string;
  rows: { item_name: string; category_id?: number; status?: "概算" | "確定"; amount: number; tax_rate?: number }[];
  source_file?: string;
}
export interface PostPlImportRakutenExpenseResponse {
  ok: true;
  imported: number;
  buckets: Record<string, number>;
  unknown_items: string[];
}

export interface PostPlImportRakutenPointsInput {
  rows: { order_number: string; purchase_date: string; points: number }[];
}
export interface PostPlImportRakutenPointsResponse {
  ok: true;
  imported: number;
  matched: number;
  unmatched: string[];
  total_points: number;
}

export interface PostPlImportRakutenAffiliateInput {
  month: string;
  clicks: number;
  sales_count: number;
  sales_amount: number;
  rewards: number;
  partner_rows: number;
}
export interface PostPlImportRakutenAffiliateResponse {
  ok: true;
}

export interface PostPlImportRakutenAdPurchaseInput {
  month: string;
  rows: { ad_type_raw: string; item_name: string; amount: number; period_from?: string; period_to?: string; status?: string }[];
  source_file?: string;
}
export interface PostPlImportRakutenAdPurchaseResponse {
  ok: true;
  imported: number;
}

export interface PostPlLogiMonthlyInput {
  month: string;
  shipping_jpy: number;
  material_jpy: number;
  shipments: number;
  storage_jpy?: number;
  wms_jpy?: number;
  status: "estimate" | "actual";
  note?: string;
}
export interface PostPlLogiMonthlyResponse {
  ok: true;
}

export async function postPlImportRakutenExpense(
  input: PostPlImportRakutenExpenseInput
): Promise<PostPlImportRakutenExpenseResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlImportRakutenExpense(input));
  return realFetch<PostPlImportRakutenExpenseResponse>(`/pl/import/rakuten-expense`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function postPlImportRakutenPoints(
  input: PostPlImportRakutenPointsInput
): Promise<PostPlImportRakutenPointsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlImportRakutenPoints(input));
  return realFetch<PostPlImportRakutenPointsResponse>(`/pl/import/rakuten-points`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function postPlImportRakutenAffiliate(
  input: PostPlImportRakutenAffiliateInput
): Promise<PostPlImportRakutenAffiliateResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlImportRakutenAffiliate(input));
  return realFetch<PostPlImportRakutenAffiliateResponse>(`/pl/import/rakuten-affiliate`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function postPlImportRakutenAdPurchase(
  input: PostPlImportRakutenAdPurchaseInput
): Promise<PostPlImportRakutenAdPurchaseResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlImportRakutenAdPurchase(input));
  return realFetch<PostPlImportRakutenAdPurchaseResponse>(`/pl/import/rakuten-ad-purchase`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function postPlLogiMonthly(input: PostPlLogiMonthlyInput): Promise<PostPlLogiMonthlyResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlLogiMonthly(input));
  return realFetch<PostPlLogiMonthlyResponse>(`/pl/logi-monthly`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/**
 * 「今すぐ更新」ボタンから呼ぶ即時同期（worker `POST /pl/sync`、review §2-7 の穴埋め）。
 * body: { what: 'cost' | 'meta' | 'google' | 'amazon' | 'finance' | 'freee' }。worker側レスポンスは `{ ok:true, result }`
 * （中身は what ごとに違う集計オブジェクトで、console側は件数等の詳細表示までは持たない）。
 */
export type PlSyncWhat = "cost" | "meta" | "google" | "amazon" | "finance" | "freee";
export interface PostPlSyncResponse {
  ok: true;
  result: unknown;
}

export async function postPlSync(what: PlSyncWhat, since?: string): Promise<PostPlSyncResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlSync(what));
  return realFetch<PostPlSyncResponse>(`/pl/sync`, {
    method: "POST",
    body: JSON.stringify(since ? { what, since } : { what }),
  });
}

// ---- P/L「財務」タブ（freee 財務会計・stoq-worker /api/ad-console/pl/finance ほか） ----

export async function fetchPlFinance(month: string): Promise<FinanceResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlFinance(currentSearch(), month));
  const params = new URLSearchParams({ month });
  return realFetch<FinanceResponse>(`/pl/finance?${params.toString()}`);
}

export async function fetchPlFreeeStatus(): Promise<FreeeStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlFreeeStatus(currentSearch()));
  return realFetch<FreeeStatusResponse>(`/pl/freee/status`);
}

/** freee 認可コード（urn:ietf:wg:oauth:2.0:oob で表示されるコード）を worker に渡してトークン交換させる */
export async function postPlFreeeAuth(code: string): Promise<FreeeStatusResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlFreeeAuth(code));
  return realFetch<FreeeStatusResponse>(`/pl/freee/auth`, {
    method: "POST",
    body: JSON.stringify({ code }),
  });
}

export interface PostPlInventoryMonthEndInput {
  month: string;
  amount_jpy: number;
  note?: string;
}
export interface PostPlInventoryMonthEndResponse {
  ok: true;
  inventory: InventoryMonthEndRow[];
}

export async function postPlInventoryMonthEnd(
  input: PostPlInventoryMonthEndInput
): Promise<PostPlInventoryMonthEndResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlInventoryMonthEnd(input));
  return realFetch<PostPlInventoryMonthEndResponse>(`/pl/inventory-month-end`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** 倉庫在庫（S3 月末在庫報告 × 月末時点の総平均単価）から月末在庫を自動計算して保存する。written=false は手入力行を上書きしなかった */
export async function postPlInventoryMonthEndAuto(input: { month: string; force?: boolean }): Promise<InventoryAutoResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlInventoryMonthEndAuto(input));
  return realFetch<InventoryAutoResponse>(`/pl/inventory-month-end/auto`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ---- 前払い仕入（在途品）台帳（freee 輸入仕入高の明細に入荷日だけ付ける） ----

export type PostPlPrepaymentInput =
  /** 入荷日の設定／クリア（null = 未入荷に戻す） */
  | { id: string; arrived_on: string | null; note?: string }
  /** 手入力行の追加（期首前の前払い等） */
  | { issue_date: string; amount_jpy: number; description?: string; arrived_on?: string | null; note?: string }
  /** 手入力行の削除（freee 由来の行は不可） */
  | { id: string; delete: true };

export async function fetchPlPrepayments(): Promise<PrepaymentsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetPlPrepayments());
  return realFetch<PrepaymentsResponse>(`/pl/prepayments`);
}

export async function postPlPrepayment(body: PostPlPrepaymentInput): Promise<PrepaymentsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostPlPrepayment(body));
  return realFetch<PrepaymentsResponse>(`/pl/prepayments`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

// ---- 転換率・売上分析（stoq-worker /api/ad-console/analytics） ----

export interface FetchAnalyticsParams {
  from: string;
  to: string;
  granularity: "day" | "week" | "month";
  product?: string | null;
}

export async function fetchAnalytics(params: FetchAnalyticsParams): Promise<AnalyticsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAnalytics(params));
  const qs = new URLSearchParams({ from: params.from, to: params.to, granularity: params.granularity });
  if (params.product) qs.set("product", params.product);
  return realFetch<AnalyticsResponse>(`/analytics?${qs.toString()}`);
}

/** ログイン画面から呼ぶ。成功時はBFFがセッションCookieを発行する(レスポンス自体には何も含めない)。 */
/**
 * ログイン。`user` は ADCONSOLE_USERS_JSON 運用のときに必須（BFF が 422 USER_REQUIRED を返す）。
 * パスワード1本運用（USERS_JSON 未設定）では user は無視される。
 */
export async function loginWithPassword(password: string, user?: string): Promise<SessionUser> {
  const res = await fetch("/api/session/login", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(user ? { user, password } : { password }),
  });
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.ok === false) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? `ログインに失敗しました（HTTP ${res.status}）`;
    throw new ApiClientError(res.status, code, message);
  }
  return body.user as SessionUser;
}

/**
 * 自分のセッション（役割）。未ログインは null（401）。
 * mock モードは `?role=gifting` で従業員の見え方を再現できる（既定 admin）。
 */
export async function fetchSessionMe(): Promise<SessionUser | null> {
  if (isMockMode()) {
    const role = currentSearch().get("role");
    return isRole(role)
      ? { sub: role === "admin" ? "murase" : "kitamura", role, name: role === "admin" ? "村瀬（mock）" : "北村（mock）" }
      : { sub: "murase", role: "admin", name: "村瀬（mock）" };
  }
  const res = await fetch("/api/session/me", { credentials: "include", headers: { Accept: "application/json" } });
  if (res.status === 401) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok || !body || body.ok === false || !isRole(body.user?.role)) {
    const code = body?.error?.code ?? "UNKNOWN";
    const message = body?.error?.message ?? `セッション確認に失敗しました（HTTP ${res.status}）`;
    throw new ApiClientError(res.status, code, message);
  }
  return body.user as SessionUser;
}

export async function logout(): Promise<void> {
  if (isMockMode()) return;
  await fetch("/api/session/logout", { method: "POST", credentials: "include" }).catch(() => undefined);
}

// ---- レビュー分析・返信対応（stoq-worker /api/ad-console/reviews/*・
// tmp/fable/2026-09-27-review-reply/plan.md §2-4/§3） ----
// review_key（'rk:123' | 'sh:123'）はコロンを含むため、パスに埋め込む際は必ず encodeURIComponent する。

export interface ReviewQueryParams {
  period: ReviewPeriodMode;
  month: string;
  store: ReviewStoreFilter;
}

function reviewQs(params: ReviewQueryParams, extra?: Record<string, string>): string {
  const qs = new URLSearchParams({ period: params.period, month: params.month, store: params.store, ...extra });
  return qs.toString();
}

export async function fetchReviewOverview(params: ReviewQueryParams): Promise<ReviewOverviewResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewOverview(params));
  return realFetch<ReviewOverviewResponse>(`/reviews/overview?${reviewQs(params)}`);
}

export async function fetchReviewList(
  params: ReviewQueryParams & { filter: ReviewListFilter; q?: string }
): Promise<ReviewListResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewList(params));
  const extra: Record<string, string> = { filter: params.filter };
  if (params.q) extra.q = params.q;
  return realFetch<ReviewListResponse>(`/reviews/list?${reviewQs(params, extra)}`);
}

export async function fetchReviewItem(key: string): Promise<ReviewItemDetailResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewItem(key));
  return realFetch<ReviewItemDetailResponse>(`/reviews/item/${encodeURIComponent(key)}`);
}

export async function postReviewDraft(key: string, instruction?: string): Promise<ReviewDraftResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewDraft(key, instruction));
  return realFetch<ReviewDraftResponse>(`/reviews/item/${encodeURIComponent(key)}/draft`, {
    method: "POST",
    body: JSON.stringify(instruction ? { instruction } : {}),
  });
}

/**
 * `force:true` はNGワード検出（422 `{error:{code:'ng_words'},ngHits}`）後の「このまま投稿」再送信に使う。
 * ApiClientError.details.ngHits でヒット語を受け取れる（realFetch/wrapMock共通のdetails抽出規約）。
 */
export async function postReviewReply(
  key: string,
  body: string,
  source: "manual" | "ai",
  force?: boolean
): Promise<ReviewReplyResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewReply(key, body, source, force));
  return realFetch<ReviewReplyResponse>(`/reviews/item/${encodeURIComponent(key)}/reply`, {
    method: "POST",
    body: JSON.stringify({ body, source, ...(force ? { force: true } : {}) }),
  });
}

export async function postReviewUnqueue(key: string): Promise<ReviewUnqueueResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewUnqueue(key));
  return realFetch<ReviewUnqueueResponse>(`/reviews/item/${encodeURIComponent(key)}/unqueue`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function postReviewFlow(key: string, input: ReviewFlowInput): Promise<ReviewFlowResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewFlow(key, input));
  return realFetch<ReviewFlowResponse>(`/reviews/item/${encodeURIComponent(key)}/flow`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function fetchReviewRules(): Promise<ReviewRulesResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewRules());
  return realFetch<ReviewRulesResponse>(`/reviews/rules`);
}

export async function postReviewRule(input: ReviewRuleUpdateInput): Promise<ReviewRulesResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewRule(input));
  return realFetch<ReviewRulesResponse>(`/reviews/rules`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function postReviewSettings(slaHours: ReviewSlaHours): Promise<ReviewRulesResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewSettings(slaHours));
  return realFetch<ReviewRulesResponse>(`/reviews/settings`, {
    method: "POST",
    body: JSON.stringify({ slaHours }),
  });
}

export async function fetchReviewReport(params: ReviewQueryParams): Promise<ReviewReportResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewReport(params));
  return realFetch<ReviewReportResponse>(`/reviews/report?${reviewQs(params)}`);
}

export async function postReviewReportGenerate(month: string): Promise<ReviewReportGenerateResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostReviewReportGenerate(month));
  return realFetch<ReviewReportGenerateResponse>(`/reviews/report/generate`, {
    method: "POST",
    body: JSON.stringify({ month }),
  });
}

export async function fetchReviewCampaigns(month: string): Promise<ReviewCampaignsResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetReviewCampaigns(month));
  const qs = new URLSearchParams({ month });
  return realFetch<ReviewCampaignsResponse>(`/reviews/campaigns?${qs.toString()}`);
}

// ---- Amazon商品管理（tmp/fable/2026-10-04-amazon-product-manager/plan.md §4・§5） ----

const amzSku = (sku: string) => encodeURIComponent(sku);

export async function fetchAmazonOverview(): Promise<AmzOverviewResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonOverview());
  return realFetch<AmzOverviewResponse>(`/amazon/overview`);
}

/** 全体同期（出品キャッシュ更新＋各SKUの取得・マージ）。overview を返す。 */
export async function postAmazonSync(): Promise<AmzOverviewResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostAmazonSync());
  return realFetch<AmzOverviewResponse>(`/amazon/sync`, { method: "POST", body: JSON.stringify({}) });
}

export async function fetchAmazonProduct(sku: string): Promise<AmzProductDetailResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonProduct(sku));
  return realFetch<AmzProductDetailResponse>(`/amazon/products/${amzSku(sku)}`);
}

export async function postAmazonProductSync(sku: string): Promise<AmzProductSyncResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPostAmazonProductSync(sku));
  return realFetch<AmzProductSyncResponse>(`/amazon/products/${amzSku(sku)}/sync`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function saveAmazonDraft(sku: string, input: AmzSaveDraftInput): Promise<AmzSaveDraftResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockSaveAmazonDraft(sku, input));
  return realFetch<AmzSaveDraftResponse>(`/amazon/products/${amzSku(sku)}/draft`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** 下書きを Amazon 現在値に戻す（未登録SKUは 422）。レスポンスの形は使わず、呼び出し側で再取得する。 */
export async function discardAmazonDraft(sku: string): Promise<{ ok: true }> {
  if (isMockMode()) return wrapMock((mock) => mock.mockDiscardAmazonDraft(sku));
  return realFetch<{ ok: true }>(`/amazon/products/${amzSku(sku)}/discard`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function importAmazonProduct(input: AmzImportInput): Promise<AmzProductDetailResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockImportAmazonProduct(input));
  return realFetch<AmzProductDetailResponse>(`/amazon/products/import`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function previewAmazonPublish(sku: string): Promise<AmzPreviewResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPreviewAmazonPublish(sku));
  return realFetch<AmzPreviewResponse>(`/amazon/products/${amzSku(sku)}/preview`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

/** Amazonへ反映。writeConfirmed:true と、preview が返した changesHash が必須（worker 側でも検証）。 */
export async function publishAmazon(sku: string, input: AmzPublishInput): Promise<AmzPublishResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockPublishAmazon(sku, input));
  return realFetch<AmzPublishResponse>(`/amazon/products/${amzSku(sku)}/publish`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function askAmazonCopilot(sku: string, input: AmzAiInput): Promise<AmzAiResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockAskAmazonCopilot(sku, input));
  return realFetch<AmzAiResponse>(`/amazon/products/${amzSku(sku)}/ai`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function fetchAmazonHistory(opts?: { sku?: string; limit?: number }): Promise<AmzHistoryResponse> {
  if (isMockMode()) return wrapMock((mock) => mock.mockGetAmazonHistory(opts));
  const qs = new URLSearchParams();
  if (opts?.sku) qs.set("sku", opts.sku);
  if (opts?.limit) qs.set("limit", String(opts.limit));
  const q = qs.toString();
  return realFetch<AmzHistoryResponse>(`/amazon/history${q ? `?${q}` : ""}`);
}
